import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { emailBuilderSyncEditorDocument } from "redux/actions/emailBuilder/emailBuilder";
import {
  emailDocumentsEqual,
} from "pages/secure/facility/marketing/emailBuilder/emailBuilderAutosaveBaseline";
import {
  isEmailBuilderSettingsAutosaveDeferred,
  subscribeEmailBuilderSettingsAutosaveDeferral,
} from "pages/secure/facility/marketing/emailBuilder/emailBuilderSettingsTextAutosaveDeferral";
import { editorReduxStore } from "../../domain/store";

export function useEmailBuilderEditorDocumentAutosaveSync(
  editorSyncReady: boolean,
): void {
  const dispatch = useAppDispatch();
  const loadStatus = useAppSelector(store => store.emailBuilderStore.loadStatus);
  const autoSaveConflict = useAppSelector(store => store.emailBuilderStore.autoSaveConflict);
  const adminDocument = useAppSelector(store => store.emailBuilderStore.document);
  const adminDocumentRef = useRef(adminDocument);

  useEffect(() => {
    adminDocumentRef.current = adminDocument;
  }, [adminDocument]);

  useEffect(() => {
    if (loadStatus !== "succeeded" || autoSaveConflict || !editorSyncReady) {
      return;
    }

    function syncFromEditorDocument(): void {
      const editorState = editorReduxStore.getState().editor;
      const editorDocument = editorState.document;
      const currentAdmin = adminDocumentRef.current;
      const inlineEditingActive = editorState.textEditingBlock !== null;
      const settingsEditingActive = isEmailBuilderSettingsAutosaveDeferred();

      const adminMatchesEditor =
        currentAdmin !== null &&
        emailDocumentsEqual(editorDocument, currentAdmin);

      if (inlineEditingActive || settingsEditingActive || adminMatchesEditor) {
        return;
      }

      dispatch(emailBuilderSyncEditorDocument(editorDocument));
    }

    const unsubscribeEditor = editorReduxStore.subscribe(syncFromEditorDocument);
    const unsubscribeSettingsDeferral =
      subscribeEmailBuilderSettingsAutosaveDeferral(syncFromEditorDocument);

    return () => {
      unsubscribeEditor();
      unsubscribeSettingsDeferral();
    };
  }, [autoSaveConflict, dispatch, editorSyncReady, loadStatus]);
}
